import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingMsg, setPendingMsg] = useState("");

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPendingMsg("");
    setLoading(true);
    try {
      // Backend returns { success, pending, message } — no auto login
      const { api } = await import("@/lib/api");
      const res = await api.post("/auth/register", { email, password, name });
      if (res.data?.pending) {
        setPendingMsg(res.data.message || "Kayıt alındı. Admin onayı bekleniyor.");
        toast.success("Kayıt alındı — admin onayı bekleniyor");
      } else {
        toast.success("Kayıt başarılı");
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="register-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        <div className="mb-8">
          <Logo size={44} showWordmark />
        </div>

        <h1 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Kayıt Ol</h1>
        <p className="text-slate-600 mb-6 text-sm">
          Kayıt sonrası hesabınız <span className="font-bold text-amber-700">admin onayı</span> bekleyecektir. Onay verildikten sonra giriş yapabilirsiniz.
        </p>

        {pendingMsg && (
          <div className="p-3 mb-4 bg-amber-50 border-2 border-amber-300 text-amber-900 text-sm rounded-md" data-testid="register-pending">
            <div className="font-bold mb-1">✅ Kayıt Alındı</div>
            {pendingMsg}
            <div className="mt-2 text-xs">Onaylandığında email adresinizle giriş yapabilirsiniz. <Link to="/login" className="underline font-bold">Giriş sayfasına dön</Link></div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="name" className="text-slate-700 font-semibold text-sm">Ad Soyad</Label>
            <Input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 h-12 border-2 border-slate-300 focus-visible:ring-sky-500"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 h-12 border-2 border-slate-300 focus-visible:ring-sky-500"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Şifre (en az 6 karakter)</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-12 border-2 border-slate-300 focus-visible:ring-sky-500"
              data-testid="register-password-input"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border-2 border-red-300 text-red-800 text-sm rounded-md" data-testid="register-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-md"
            data-testid="register-submit-button"
          >
            {loading ? <Loader2 className="animate-spin" /> : "Kayıt Ol"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-slate-600 text-center">
          Zaten hesabınız var mı?{" "}
          <Link to="/login" className="text-sky-500 font-bold hover:underline" data-testid="login-link">
            Giriş yapın
          </Link>
        </div>
      </div>
    </div>
  );
}
